import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, Users, Apple, BookOpen, NotebookText, LogOut, User } from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/patients", label: "Patients", icon: Users, testid: "nav-patients" },
  { to: "/diet-plans", label: "Diet Plans", icon: NotebookText, testid: "nav-diet-plans" },
  { to: "/recipes", label: "Recipes", icon: BookOpen, testid: "nav-recipes" },
  { to: "/foods", label: "Food Database", icon: Apple, testid: "nav-foods" },
  { to: "/profile", label: "Profile", icon: User, testid: "nav-profile" },
];

export default function DietitianLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = async () => {
    await logout();
    navigate("/dietitian/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-background" data-testid="dietitian-layout">
      {/* Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 border-r border-border bg-card sticky top-0 h-screen">
        <div className="p-6 border-b border-border">
          <Logo size="md" />
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/dashboard"}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-secondary text-secondary-foreground"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`
                }
              >
                <Icon size={18} />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-4 border-t border-border">
          <div className="px-2 mb-3">
            <div className="text-sm font-semibold text-foreground" data-testid="user-name">{user?.name}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          </div>
          <Button variant="outline" className="w-full justify-start gap-2" onClick={handleLogout} data-testid="logout-button">
            <LogOut size={16} /> Log out
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-card border-b border-border flex items-center justify-between px-4 py-3">
        <Logo size="sm" />
        <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="mobile-logout-button">
          <LogOut size={16} />
        </Button>
      </div>

      <main className="flex-1 lg:pl-0 pt-16 lg:pt-0 pb-20 lg:pb-0">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-card border-t border-border flex items-center justify-around py-2">
        {NAV.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/dashboard"}
              data-testid={`mobile-${item.testid}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 px-3 py-2 text-xs ${isActive ? "text-primary" : "text-muted-foreground"}`
              }
            >
              <Icon size={18} />
              <span>{item.label.split(" ")[0]}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
